import * as Phaser from "phaser"
import { GAME_HEIGHT, GAME_WIDTH } from "../../data/constants"
import type { EnemyData, GameSprite } from "../../types"
import { type EnemyState, StatefulEnemyRuntime } from "../enemy-system"
import type { VfxManager } from "../vfx-manager"

const FIRE_DISTANCE = 400

export type OrbDroneMovementPatternLabel =
  | "DARIUS_CONVOY"
  | "GRADIUS_SWEEP"
  | "RTYPE_PEEL"

export type OrbDroneConvoySpawnOverrides = Partial<EnemyData> & {
  convoyStopX: number
  convoyIndex: number
  convoyShotDelayMs: number
  convoyPatternTimer: number
  convoyPatternPhase: "attackRun"
  convoyShiftCount: number
  convoyLoopDirection: number
  homeY: number
  movementPatternLabel: OrbDroneMovementPatternLabel
  arcadeMovementPattern: OrbDroneMovementPatternLabel
  convoyReentryY: number
  y: number
}

export function getOrbDroneMovementPatternLabel(
  index = 0,
  waveNumber = 1,
): OrbDroneMovementPatternLabel {
  if (waveNumber !== 1) return "DARIUS_CONVOY"

  const patterns: OrbDroneMovementPatternLabel[] = [
    "DARIUS_CONVOY",
    "GRADIUS_SWEEP",
    "RTYPE_PEEL",
  ]
  return patterns[index % patterns.length]
}

export function emitOrbDroneAttackEffects(
  vfx: Pick<VfxManager, "sparkEmitter" | "emitProjectileTrail">,
  originX: number,
  originY: number,
): void {
  vfx.sparkEmitter.explode(4, originX, originY)
  vfx.emitProjectileTrail(originX, originY, 0x22d3ee, 1)
}

export function getOrbDroneAngle(time: number): number {
  return time / 34
}

export function getOrbDroneConvoyCount(
  waveNumber: number,
  maxCount: number,
): number {
  const preferredCount = waveNumber === 1 ? 9 : 4
  return Math.max(1, Math.min(preferredCount, maxCount))
}

export function createOrbDroneConvoyOverrides(
  index: number,
  convoyCount: number,
  stopBaseX: number,
  convoyLineSpacingX: number,
  formationCenterY: number,
  startY: number,
  waveNumber: number,
): OrbDroneConvoySpawnOverrides {
  const centerOffset = index - (convoyCount - 1) / 2
  const movementPattern = getOrbDroneMovementPatternLabel(index, waveNumber)
  const holdOffsetY = centerOffset * (waveNumber === 1 ? 20 : 28)
  const holdY = Phaser.Math.Clamp(
    formationCenterY + holdOffsetY,
    50,
    GAME_HEIGHT - 50,
  )

  return {
    convoyStopX: stopBaseX + centerOffset * convoyLineSpacingX,
    convoyIndex: index,
    convoyShotDelayMs: index * 120,
    convoyPatternTimer: 2600 + index * 220,
    convoyPatternPhase: "attackRun",
    convoyShiftCount: 0,
    convoyLoopDirection: index % 2 === 0 ? -1 : 1,
    homeY: holdY,
    movementPatternLabel: movementPattern,
    arcadeMovementPattern: movementPattern,
    convoyReentryY: Phaser.Math.Clamp(
      holdY - holdOffsetY * 0.35,
      50,
      GAME_HEIGHT - 50,
    ),
    y: startY,
  }
}

export function spawnOrbDroneConvoy(options: {
  baseX: number
  baseY: number
  maxCount: number
  difficultyMultiplier: number
  waveNumber: number
  source: "encounter" | "fallback" | "batch"
  createEnemyByType: (
    type: "orbDrone",
    x: number,
    y: number,
    wave: number,
    difficultyMultiplier: number,
  ) => {
    vx: number
    vy: number
    hp: number
    damage: number
    points: number
    shootCooldown: number
  }
  spawnConfiguredEnemy: (
    enemyType: "orbDrone",
    x: number,
    y: number,
    overrides: Partial<EnemyData>,
    options?: {
      introScale?: number
      fadeIn?: boolean
      tweenDurationMs?: number
      depth?: number
    },
  ) => GameSprite | null
  observeSpawn: (
    sprite: GameSprite,
    source: "encounter" | "fallback" | "batch",
  ) => void
}): number {
  const convoyCount = getOrbDroneConvoyCount(
    options.waveNumber,
    options.maxCount,
  )
  const entrySpacingX = options.waveNumber === 1 ? 16 : 18
  const stopBaseX = options.baseX - 300
  const convoyLineSpacingX = 40
  let spawned = 0

  for (let i = 0; i < convoyCount; i += 1) {
    const centerOffset = i - (convoyCount - 1) / 2
    const entryOffsetX = centerOffset * entrySpacingX
    const x = options.baseX + entryOffsetX
    const y = Phaser.Math.Clamp(options.baseY, 50, GAME_HEIGHT - 50)
    const orbEnemy = options.createEnemyByType(
      "orbDrone",
      x,
      y,
      options.waveNumber,
      options.difficultyMultiplier,
    )
    const sprite = options.spawnConfiguredEnemy(
      "orbDrone",
      x,
      y,
      {
        vx: orbEnemy.vx,
        vy: orbEnemy.vy,
        hp: orbEnemy.hp,
        damage: orbEnemy.damage,
        points: orbEnemy.points,
        shootCooldown: orbEnemy.shootCooldown,
        ...createOrbDroneConvoyOverrides(
          i,
          convoyCount,
          stopBaseX,
          convoyLineSpacingX,
          options.baseY,
          y,
          options.waveNumber,
        ),
      },
      {
        introScale: 1,
        fadeIn: true,
        tweenDurationMs: 0,
        depth: 70,
      },
    )
    if (sprite) {
      options.observeSpawn(sprite, options.source)
      spawned += 1
    }
  }

  return spawned
}

export class OrbDroneEnemy extends StatefulEnemyRuntime<"active"> {
  private hasFired = false

  public constructor(sprite: GameSprite, enemyData: EnemyData) {
    const states: EnemyState<"active">[] = [
      {
        name: "active",
        behaviorState: "flying",
        animationState: "flying",
        update: (context) => this.updateMovement(context),
      },
    ]

    super(sprite, enemyData, "active", states)
  }

  private updateMovement(context: {
    sprite: GameSprite
    enemyData: EnemyData
    time: number
    delta: number
    scrollCompensation: number
  }): null {
    const { sprite, enemyData, delta, scrollCompensation, time } = context
    const hasEnteredRange = sprite.x < GAME_WIDTH - FIRE_DISTANCE

    if (!this.hasFired && hasEnteredRange && sprite.x > 50) {
      enemyData.canFire = true
      this.hasFired = true
    }

    sprite.setAngle(getOrbDroneAngle(time))

    const convoyIndex = enemyData.convoyIndex ?? 0
    const baseOffset = convoyIndex * 120
    const wave = Math.sin((time + baseOffset) / 400) * 25

    const targetY = (enemyData.homeY ?? sprite.y) + wave
    const ease = 1 - (1 - 0.12) ** (delta / 16.6)
    sprite.y = Phaser.Math.Linear(sprite.y, targetY, ease)

    enemyData.vx = scrollCompensation - 3.5

    return null
  }

  public override onHeroCollision():
    | "damage-only"
    | "damage-and-defeat"
    | null {
    return "damage-and-defeat"
  }
}
